import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/outline';
import { useSelector } from 'react-redux';
import { apiZedocSearchProjects } from '../../common/api/zedoc';
import ProjectSelect from '../../common/selectors/Project';
import PageBar from '../../components/Layout/PageBar';
import Table from '../../components/lists/Table';
import Search, { SearchHighlighter } from '../../components/inputs/Search';
import branding from '../../utils/branding';
import Tooltip from '../../common/components/Tooltip';
import Skeleton from '../../components/Skeleton';
import usePagination from '../../utils/usePagination';
import useDocumentTitle from '../../utils/useDocumentTitle';
import { getFilters } from '../ProjectsList/store';
import Card from '../../components/Card';

const PatientsInProject = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const handleOnOpenProject = (_id) =>
    history.push(`/patients-in-project/${_id}`);

  const filters = useSelector(getFilters);
  const {
    ready: subscriptionsReady,
    items: projects,
    totalItems,
    getPaginationProps,
  } = usePagination({
    // Back to the first page whenever the filters change, see usePagination.
    resetPageKey: filters,
    debounceMs: 500,
    selector: ProjectSelect,
    getSubscription: (currentPage, resultsPerPage) =>
      apiZedocSearchProjects.withParams({
        filters,
        controlId: '$meta.id',
        pageIndex: currentPage - 1,
        resultsPerPage,
      }),
  });
  const projectsColumns = [
    {
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <div className="text-primary-700 font-medium h-10 flex items-center">
          <SearchHighlighter text={text} />
        </div>
      ),
    },
  ];

  const title = t('recipientsInProject', { context: branding });
  useDocumentTitle([title]);

  return (
    <div className="-mx-4 -my-6">
      <PageBar title={title} isNewPresentation />
      <div className="p-4">
        <Card className="stack-4">
          <div className="stack-2">
            <div className="cluster-2 items-center">
              <span className="text-sm">
                {t('forms:searchByProjectName.label')}
              </span>
              <Tooltip title={t('forms:searchByProjectName.tooltip')}>
                <InformationCircleIcon className="w-4 h-4" />
              </Tooltip>
            </div>
            <Search placeholder={t('forms:searchByProjectName.placeholder')} />
          </div>
          <div className="cluster-4 items-center justify-between">
            <span className="text-lg font-medium">
              {subscriptionsReady ? (
                <span>{`#${t('projectsWithCount', {
                  count: totalItems,
                })}`}</span>
              ) : (
                <Skeleton className="h-6 w-40" block={false} />
              )}
            </span>
          </div>
          <Table
            data-testid="projects-table"
            rowKey="_id"
            dataSource={projects}
            columns={projectsColumns}
            loading={!subscriptionsReady}
            pagination={getPaginationProps()}
            onRow={({ _id }) => ({
              onClick: () => handleOnOpenProject(_id),
            })}
          />
        </Card>
      </div>
    </div>
  );
};

export default PatientsInProject;
